import * as React from "react";
import { Box, CircularProgress, Link, Stack, TextField, Typography } from "@mui/material";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import axios from "axios";
import { authHeader } from "../../authClient";
import { prepareSignupProof, signUpWithPasskey, browserSupportsWebAuthn } from "../../auth/passkeys";
import { NAME_MAX, nameLooksValid } from "../../auth/accountName";
import {
  AuthLayout, Eyebrow, Heading, Notice, PrimaryButton, LineButton, DestinationList, LegalLinks, fieldSx, linkSx
} from "../authShared";
import { RecoveryCodeView } from "./RecoveryCodeView";

// ─────────────────────────────────────────────────────────────────────────────
// Create an account: a name, no email, no password.
//
//   1. Passkey   — the account's name (required, PLAN.md), then one tap; the
//                  proof-of-work against bulk sign-ups has been running
//                  invisibly since the page opened.
//   2. Recovery  — the code, shown once.
//   3. Email     — optional, for security alerts and receipts only.
// ─────────────────────────────────────────────────────────────────────────────

const POINTS = [
  ["No password", "Your phone or computer unlocks Fennira with your fingerprint, face or screen lock."],
  ["Just a name", "Name the account (you, your studio or your show). No email, no phone number."],
  ["A way back", "You'll get a recovery code in case you lose your devices."],
];

export default function Signup() {
  const navigate = useNavigate();
  const supported = React.useMemo(() => browserSupportsWebAuthn(), []);
  const [step, setStep] = React.useState("passkey"); // passkey → code → email
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const [code, setCode] = React.useState("");
  const [name, setName] = React.useState("");
  const proof = React.useRef(null);

  // Start the proof-of-work now, while the page is being read.
  React.useEffect(() => {
    if (!supported) return;
    proof.current = prepareSignupProof();
    proof.current.catch(() => { proof.current = null; }); // retried on click
  }, [supported]);

  const create = async () => {
    if (!nameLooksValid(name)) return;
    setBusy(true);
    setError("");
    try {
      const { recoveryCode } = await signUpWithPasskey(proof.current, name);
      setCode(recoveryCode);
      setStep("code");
    } catch (err) {
      // A proof can only be used once; make a fresh one for the next try.
      proof.current = prepareSignupProof();
      proof.current.catch(() => { proof.current = null; });
      if (!err.cancelled) setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const leave = React.useCallback(() => {
    setCode("");
    navigate("/home", { replace: true });
  }, [navigate]);

  return (
    <AuthLayout
      maxWidth={460}
      panel={{
        eyebrow: "Create your studio",
        heading: "Every platform, one passkey.",
        body: "Stream once to YouTube, Facebook, LinkedIn, X, Kick and any RTMP server. Your account is a passkey on your device: nothing to remember, nothing to leak.",
        extra: <DestinationList />,
      }}
    >
      {step === "passkey" && (
        <>
          <Eyebrow>New account</Eyebrow>
          <Heading>Create your account</Heading>
          <Stack spacing={1.75} sx={{ my: 3.5 }}>
            {POINTS.map(([title, text]) => (
              <Stack key={title} direction="row" spacing={1.5} alignItems="flex-start">
                <CheckCircleIcon sx={{ color: "var(--fn-teal)", fontSize: 22, mt: "1px" }} />
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 15.5 }}>{title}</Typography>
                  <Typography sx={{ color: "var(--fn-muted-l)", fontSize: 14.5, lineHeight: 1.5 }}>{text}</Typography>
                </Box>
              </Stack>
            ))}
          </Stack>

          {error && <Notice onClose={() => setError("")}>{error}</Notice>}
          {!supported && (
            <Notice severity="warning">
              This browser can't create passkeys. Open Fennira in an up-to-date Chrome, Safari, Edge or Firefox.
            </Notice>
          )}

          <TextField fullWidth label="Account name" placeholder="Account name or your name"
            value={name} onChange={(e) => setName(e.target.value)} disabled={busy}
            onKeyDown={(e) => { if (e.key === "Enter") create(); }}
            helperText="Shown in the Studio and on your passkey. You can change it later."
            inputProps={{ maxLength: NAME_MAX, autoComplete: "organization" }}
            sx={{ ...fieldSx, mb: 2.5, "& .MuiFormHelperText-root": { fontFamily: "var(--fn-sans)", color: "var(--fn-muted-l)", mx: 0.5 } }} />

          <PrimaryButton onClick={create} disabled={busy || !supported || !nameLooksValid(name)} startIcon={!busy && <FingerprintIcon />}>
            {busy ? <CircularProgress size={21} thickness={5} sx={{ color: "var(--fn-cream)" }} /> : "Create my passkey"}
          </PrimaryButton>
          <Typography sx={{ mt: 1.75, fontSize: 13.5, lineHeight: 1.5, color: "var(--fn-muted-l)", textAlign: "center" }}>
            By creating an account you accept the{" "}
            <Link component={RouterLink} to="/terms" sx={linkSx}>Terms of Service</Link> and confirm you are 16 or
            over. The <Link component={RouterLink} to="/privacy" sx={linkSx}>Privacy Policy</Link> explains what we keep.
          </Typography>
          <Typography sx={{ mt: 2.5, fontSize: 14.5, color: "var(--fn-muted-l)", textAlign: "center" }}>
            Already have an account?{" "}
            <Link component={RouterLink} to="/login" sx={linkSx}>Sign in</Link>
          </Typography>
        </>
      )}

      {step === "code" && (
        <>
          <Eyebrow>Step 2 of 3</Eyebrow>
          <Box sx={{ mt: 2.5 }}>
            <RecoveryCodeView code={code} title="Save your recovery code" onContinue={() => { setCode(""); setStep("email"); }} />
          </Box>
        </>
      )}

      {step === "email" && <AlertEmailStep onDone={leave} />}
      {step === "passkey" && <LegalLinks />}
    </AuthLayout>
  );
}

/** Optional: an address for security alerts and receipts, confirmed with a code. */
export function AlertEmailStep({ onDone }) {
  const [address, setAddress] = React.useState("");
  const [pending, setPending] = React.useState("");
  const [otp, setOtp] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");

  const send = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await axios.post("/api/me/email", { address: address.trim() }, { headers: authHeader() });
      setPending(data.pending);
    } catch (err) {
      setError(err?.response?.data?.message || "We couldn't send the code. Check the address.");
    } finally { setBusy(false); }
  };

  const confirm = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await axios.post("/api/me/email/verify", { code: otp.trim() }, { headers: authHeader() });
      onDone();
    } catch (err) {
      setError(err?.response?.data?.message || "That code isn't right.");
      setBusy(false);
    }
  };

  return (
    <>
      <Eyebrow>Step 3 of 3 · optional</Eyebrow>
      <Heading sx={{ fontSize: { xs: 32, sm: 40 } }}>Get security alerts</Heading>
      <Typography sx={{ color: "var(--fn-muted-l)", fontSize: 15.5, lineHeight: 1.55, mb: 3 }}>
        Add an email address and we'll tell you if your recovery code is used or a passkey is added. It's only for
        alerts and receipts: it can never be used to sign in or to recover your account.
      </Typography>
      {error && <Notice onClose={() => setError("")}>{error}</Notice>}

      {!pending ? (
        <Box component="form" onSubmit={send} noValidate>
          <TextField fullWidth label="Email for alerts" type="email" autoComplete="email" value={address}
            onChange={(e) => setAddress(e.target.value)} disabled={busy} sx={{ ...fieldSx, mb: 2 }} />
          <PrimaryButton type="submit" disabled={busy || !address.includes("@")}>Send a confirmation code</PrimaryButton>
        </Box>
      ) : (
        <Box component="form" onSubmit={confirm} noValidate>
          <Typography sx={{ fontSize: 15, mb: 2 }}>We sent a 6-digit code to <b>{pending}</b>.</Typography>
          <TextField fullWidth label="6-digit code" inputMode="numeric" autoComplete="one-time-code" value={otp}
            inputProps={{ maxLength: 6 }}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} disabled={busy} sx={{ ...fieldSx, mb: 2 }} />
          <PrimaryButton type="submit" disabled={busy || otp.length !== 6}>Confirm</PrimaryButton>
        </Box>
      )}
      <LineButton onClick={onDone} disabled={busy} sx={{ mt: 1.5 }}>Skip for now</LineButton>
    </>
  );
}
